// Step 22 — pure helpers for the buyer intelligence workspace.
//
// All logic here is deterministic and side-effect free. It composes
// data the backend already emits (`aggregate_buyer_intelligence` +
// `compute_deal_situation`) into shapes the buyer components render.
//
// Guardrails:
//   - No invented scores, probabilities, conversion rates.
//   - `fact_or_inference` labels are preserved VERBATIM — never
//     upgraded, never downgraded.
//   - Provenance objects are passed through untouched. If a source is
//     missing, we return null rather than a fabricated citation.
//   - Attention flags are *observations* (silent, has open items,
//     recurring pattern, bounced) — not a synthesized numeric score.

import { KIND, SEVERITY } from "../intelligence/helpers";

// ── Attention flags ────────────────────────────────────────────────
//
// Each flag is a deterministic derivation from data the backend
// already gave us. We render these as chips; we never combine them
// into a "buyer score".

export function buyerAttentionFlags(row, { patternsForBuyer = [] } = {}) {
  if (!row || !row.acquirer) return [];
  const flags = [];

  if (row.silence?.is_silent) {
    flags.push({
      kind: "silent",
      severity: SEVERITY.MEDIUM,
      label: `Tyst ${row.silence.days_since_last_outbound ?? "?"} dagar`,
      title:
        "Ingen respons på senaste utskick över tröskeln " +
        `${row.silence.threshold_days ?? 14} dagar.`,
    });
  }

  const openCount = row.open_items_count ?? 0;
  if (openCount > 0) {
    flags.push({
      kind: "open_items",
      severity: SEVERITY.MEDIUM,
      label: `${openCount} öppen post${openCount === 1 ? "" : "er"}`,
      title:
        "Frågor, informationsförfrågningar, invändningar eller krav " +
        "som väntar på svar.",
    });
  }

  const bounces = row.communication?.bounce_count ?? 0;
  if (bounces > 0) {
    flags.push({
      kind: "bounces",
      severity: SEVERITY.HIGH,
      label: `${bounces} leveransfel`,
      title: "Utskick har studsat eller markerats som klagomål.",
    });
  }

  if (patternsForBuyer.length > 0) {
    flags.push({
      kind: "in_pattern",
      severity: SEVERITY.LOW,
      label: `${patternsForBuyer.length} mönster`,
      title:
        "Köparen ingår i återkommande mönster (informationsförfrågningar, " +
        "invändningar, krav eller frågor) tillsammans med andra köpare.",
    });
  }

  return flags;
}

// ── Cross-buyer patterns filtered to one buyer ──────────────────────

const _PATTERN_GROUPS = [
  { key: "information_requested", label: "Informationsförfrågningar" },
  { key: "objection", label: "Invändningar" },
  { key: "requirement", label: "Krav" },
  { key: "question", label: "Frågor" },
];

export function crossPatternsForBuyer(patterns, acquirerId) {
  if (!patterns || acquirerId == null) return [];
  const id = Number(acquirerId);
  const out = [];
  for (const { key, label } of _PATTERN_GROUPS) {
    for (const p of patterns[key] || []) {
      const acqIds = (p.acquirer_ids || []).map((x) => Number(x));
      if (acqIds.includes(id)) {
        out.push({
          group: key,
          group_label: label,
          normalized_text: p.normalized_text,
          acquirer_count: p.acquirer_count,
          other_acquirer_ids: acqIds.filter((x) => x !== id),
        });
      }
    }
  }
  return out;
}

// ── Buyer intel items → finding objects for AIFindingCard ──────────
//
// The buyer intel aggregate returns lists of items already labeled
// with fact_or_inference and full provenance. We flatten those into
// the shared finding shape from Step 19 (helpers.js) so AIFindingCard
// can render them exactly like a risk or preparedness gap.
//
// Ordering: objections + requirements first (higher intent-to-block),
// then questions and info requests, then next-steps and uncertainties.

const _CATEGORY_META = {
  objections: { label: "Invändning", severity: SEVERITY.HIGH, order: 1 },
  requirements: { label: "Krav", severity: SEVERITY.HIGH, order: 2 },
  questions_asked: { label: "Fråga", severity: SEVERITY.MEDIUM, order: 3 },
  information_requested: { label: "Informationsförfrågan", severity: SEVERITY.MEDIUM, order: 4 },
  next_steps_proposed: { label: "Föreslaget nästa steg", severity: SEVERITY.LOW, order: 5 },
  uncertainty: { label: "Osäkerhet", severity: SEVERITY.LOW, order: 6 },
};

function _findingFromItem(item, categoryKey, meta, acquirerName) {
  // Build a "source" the AIEvidencePanel can render — the item's
  // provenance chain, verbatim. If quote text exists, expose it as
  // a secondary evidence entry.
  const provenance = item.provenance || null;
  const source =
    provenance
      ? {
          source_type: provenance.source_inbound_message_id
            ? "inbound_message"
            : "buyer_intel_item",
          source_id: String(
            provenance.source_inbound_message_id ||
              provenance.source_analysis_id ||
              item.id,
          ),
          label:
            provenance.source_inbound_message_id
              ? `Inkommande #${provenance.source_inbound_message_id}`
              : `Analys v${provenance.analysis_version ?? "?"}`,
          as_of: provenance.captured_at,
        }
      : null;
  const evidence = [];
  if (item.quote) {
    evidence.push({
      kind: "quote",
      label: `Citat: "${item.quote}"`,
    });
  }
  if (provenance?.analysis_version != null) {
    evidence.push({
      kind: "analysis",
      label: `Analys v${provenance.analysis_version}`,
    });
  }

  return {
    kind: KIND.BUYER_SIGNAL,
    severity: meta.severity,
    title: item.text,
    summary: null,
    category: meta.label,
    fact_or_inference: item.fact_or_inference || null,
    source,
    evidence,
    // NOTE: no acquirer_id here — the finding IS already inside the
    // per-buyer workspace, so the "→ open buyer" link would loop.
    ask_prompt:
      `Förklara denna ${meta.label.toLowerCase()} från ${acquirerName}: ` +
      `"${item.text}". Vad ligger bakom den och vad bör vi göra?`,
    key: `buyer.${categoryKey}.${item.id}`,
  };
}

export function buyerFindingsFromIntel(intel) {
  if (!intel || !intel.acquirer) return [];
  const acquirerName = intel.acquirer.name || "köparen";
  const out = [];
  for (const [categoryKey, meta] of Object.entries(_CATEGORY_META)) {
    const items = intel[categoryKey] || [];
    for (const item of items) {
      out.push(_findingFromItem(item, categoryKey, meta, acquirerName));
    }
  }
  // Deterministic stable order — by category order, then by item id
  // for tiebreak. We don't invent a "priority score".
  return out.sort((a, b) => {
    const catA = _CATEGORY_META[a.key.split(".")[1]]?.order ?? 99;
    const catB = _CATEGORY_META[b.key.split(".")[1]]?.order ?? 99;
    if (catA !== catB) return catA - catB;
    return a.key.localeCompare(b.key);
  });
}

// ── Ask Qada prompt builders ───────────────────────────────────────

export function buildBuyerAskPrompt({
  acquirerName,
  workspaceName,
  question,
}) {
  const buyer = acquirerName || "denna köpare";
  const workspace = workspaceName ? ` i affären ${workspaceName}` : "";
  const suffix = question ? `\n\n${question}` : "";
  return (
    `Jag vill undersöka köparen ${buyer}${workspace}. ` +
    `Använd get_buyer_intelligence, get_deal_situation och relevanta ` +
    `verktyg för att förklara läget. Bevara fact/inference-etiketter ` +
    `verbatim och cita provenance när du kan.${suffix}`
  );
}

export function buildBuyerComparisonAskPrompt({
  workspaceName,
  question,
}) {
  const workspace = workspaceName ? ` i affären ${workspaceName}` : "";
  const suffix = question ? `\n\n${question}` : "";
  return (
    `Jämför de aktiva köparna${workspace}. Använd get_deal_situation för ` +
    `portföljvyn och get_buyer_intelligence per köpare där det behövs. ` +
    `Sammanfatta styrkor, svagheter, tystnadssignaler och öppna poster. ` +
    `Cita fact/inference-etiketter och provenance verbatim.${suffix}`
  );
}
